
import { formatDate } from '@/utils/dateFormatters';
import { CheckIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TenderNotification } from '@/hooks/useNotifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

interface NotificationListProps {
  notifications: TenderNotification[];
  isLoading: boolean;
  onNotificationClick: (notification: TenderNotification) => void;
  maxHeight?: string;
}

export const NotificationList = ({
  notifications,
  isLoading,
  onNotificationClick,
  maxHeight = '300px'
}: NotificationListProps) => {
  const { t, i18n } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="py-8 px-4 text-center text-muted-foreground">
        <p>{t('notifications.noNotifications')}</p>
      </div>
    );
  }

  // Helper function to ensure we have a valid title
  const getNotificationTitle = (notification: TenderNotification): string => {
    if (notification.title && notification.title !== 'D' && !notification.title.startsWith('notifications.')) {
      return notification.title;
    }
    
    // Extract title from tender data as fallback
    if (notification.tender_data) {
      const tenderData = notification.tender_data;
      if (tenderData["notice-title"]) {
        if (typeof tenderData["notice-title"] === "object") {
          const titleObj = tenderData["notice-title"];
          // Try Danish first since we're using Danish
          if (titleObj.dan && titleObj.dan[0]) {
            return titleObj.dan[0];
          } else if (titleObj.eng && titleObj.eng[0]) {
            return titleObj.eng[0];
          } else {
            // Try to get the first value from any language
            const firstKey = Object.keys(titleObj)[0];
            if (firstKey && Array.isArray(titleObj[firstKey]) && titleObj[firstKey].length > 0) {
              return titleObj[firstKey][0];
            }
          }
        } else if (typeof tenderData["notice-title"] === "string") {
          return tenderData["notice-title"];
        }
      }
      
      // Try other possible title fields
      if (tenderData.title) return tenderData.title;
    }
    
    return t('notifications.newTender');
  };

  return (
    <ScrollArea style={{ maxHeight }} className="p-2">
      <div className="space-y-1">
        {notifications.map((notification) => (
          <button
            key={notification.id}
            onClick={() => onNotificationClick(notification)}
            className={cn(
              "w-full text-left p-3 rounded-md hover:bg-accent transition-colors",
              !notification.is_read && "bg-accent/30"
            )}
          >
            <div className="flex items-start gap-2">
              <div className={cn(
                "h-2 w-2 mt-2 rounded-full flex-shrink-0",
                notification.is_read ? "bg-muted" : "bg-primary"
              )} />
              
              <div className="flex-grow overflow-hidden">
                <div className="flex items-center justify-between w-full">
                  <h4 className="font-medium text-sm truncate pr-2">
                    {getNotificationTitle(notification)}
                  </h4>
                  {notification.is_read && (
                    <CheckIcon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground truncate">
                  {notification.organization || t('notifications.unknownOrganization')}
                </p>
                
                <div className="flex items-center justify-between mt-1">
                  <div className="text-xs text-muted-foreground">
                    <span className="font-medium">{t('notifications.deadline')}:</span> {" "}
                    {notification.deadline ? formatDate(notification.deadline) : t('notifications.noDeadline')}
                  </div>
                  
                  <div className="text-xs text-primary font-medium">
                    {t('notifications.viewDetails')}
                  </div>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
};
